// scormUnified.ts
// ———————————————————————————————————————————————————————————————
// Adapter unificado para SCORM 1.2 y 2004 - SIN dependencias externas
// Comunicación directa con la API SCORM del LMS
// ———————————————————————————————————————————————————————————————

import type {
  ScormAPI,
  Version,
  Key,
  ScormConnectionState,
  ConnectionListener,
  TimeTrackingConfig
} from './types';
import { findScormAPI } from './apiDetector';
import { apiInit, apiFinish, apiGet, apiSet, apiCommit, apiGetLastError } from './apiWrapper';
import { getCMIElement, is2004 } from './keyMapper';
import { secondsToHMS, secondsToISODur, hmsToSeconds, isoDurToSeconds } from './timeUtils';
import { connectionStateManager } from './connectionState';
import { timeTracker } from './timeTracker';

// Estado global del módulo
let API: ScormAPI = null;
let scormVersion: Version | null = null;

/**
 * API pública unificada para SCORM
 */
export const scormU = {
  /**
   * Inicializa la conexión SCORM
   */
  init(config?: TimeTrackingConfig): boolean {
    // Buscar API
    const detection = findScormAPI(window);
    API = detection.api;
    scormVersion = detection.version;

    if (!API) {
      console.error("[SCORM] No se encontró API SCORM");
      connectionStateManager.setState({
        isConnected: false,
        hasAttemptedInit: true,
        version: null,
        errorMessage: "No se encontró API SCORM",
        initTimestamp: null,
      });
      return false;
    }

    console.log(`[SCORM] API encontrada. Versión: ${scormVersion}`);

    // Inicializar
    const result = apiInit(API, scormVersion!);

    // Configurar tracking
    if (config) {
      timeTracker.configure(config);
    }

    connectionStateManager.setState({
      isConnected: result,
      hasAttemptedInit: true,
      version: result ? scormVersion : null,
      errorMessage: result ? null : "No se pudo inicializar SCORM",
      initTimestamp: result ? Date.now() : null,
    });

    if (result) {
      timeTracker.startSession();
    }

    return result;
  },

  /**
   * Verifica si está conectado a SCORM
   */
  isConnected(): boolean {
    return connectionStateManager.isConnected();
  },

  /**
   * Obtiene el estado de conexión actual
   */
  getConnectionState(): ScormConnectionState {
    return connectionStateManager.getState();
  },

  /**
   * Registra un listener para cambios de conexión
   */
  onConnectionChange(callback: ConnectionListener): void {
    connectionStateManager.addListener(callback);
  },

  /**
   * Elimina un listener de cambios de conexión
   */
  offConnectionChange(callback: ConnectionListener): void {
    connectionStateManager.removeListener(callback);
  },

  /**
   * Guarda datos en el LMS (commit) con tracking de tiempo opcional
   */
  save(): boolean {
    if (timeTracker.shouldSaveTimeOnSave() && timeTracker.hasActiveSession()) {
      const sessionSeconds = timeTracker.getElapsedSeconds();
      this.setSessionTime(sessionSeconds);
    }

    const result = apiCommit(API, scormVersion!);
    console.log(result ? "[SCORM] Commit exitoso" : "[SCORM] Commit FALLÓ");
    return result;
  },

  /**
   * Commit puro sin tracking de tiempo
   */
  commit(): boolean {
    const result = apiCommit(API, scormVersion!);
    console.log(result ? "[SCORM] Commit puro exitoso" : "[SCORM] Commit puro FALLÓ");
    return result;
  },

  /**
   * Finaliza la sesión SCORM
   */
  quit(suppressSave: boolean = false): boolean {
    if (!suppressSave && timeTracker.isAutoTrackingEnabled() && timeTracker.hasActiveSession()) {
      const sessionSeconds = timeTracker.getElapsedSeconds();
      this.setSessionTime(sessionSeconds);
      this.save();
    }

    const result = apiFinish(API, scormVersion!);

    if (result) {
      connectionStateManager.setState({ isConnected: false });
    }

    timeTracker.stopSession();
    return result;
  },

  /**
   * Obtiene los segundos transcurridos en la sesión actual
   */
  getElapsedSeconds(): number {
    return timeTracker.getElapsedSeconds();
  },

  /**
   * Obtiene la configuración de tracking de tiempo
   */
  getTimeTrackingConfig(): TimeTrackingConfig {
    return timeTracker.getConfig();
  },

  /**
   * Obtiene un valor usando una clave unificada
   */
  get(key: Key): string {
    const el = getCMIElement(key, scormVersion);
    if (!el) return "";

    const value = apiGet(API, scormVersion!, el);

    if (key === "suspendData" || key === "bookmark") {
      console.log(`[SCORM] GET ${el} = "${value}" (${value?.length || 0} chars)`);
    }

    return value ?? "";
  },

  /**
   * Establece un valor usando una clave unificada
   */
  set(key: Key, value: string): boolean {
    const el = getCMIElement(key, scormVersion);
    if (!el) return false;

    if (key === "suspendData" || key === "bookmark") {
      console.log(`[SCORM] SET ${el} = "${value}" (${value?.length || 0} chars)`);
    }

    return apiSet(API, scormVersion!, el, value);
  },

  /**
   * Establece un valor usando el elemento CMI directo (sin mapeo)
   */
  setRaw(cmiElement: string, value: string): boolean {
    return apiSet(API, scormVersion!, cmiElement, value);
  },

  /**
   * Obtiene un valor usando el elemento CMI directo (sin mapeo)
   */
  getRaw(cmiElement: string): string {
    return apiGet(API, scormVersion!, cmiElement);
  },

  /**
   * Establece la ubicación/bookmark del estudiante
   */
  setBookmark(location: string): boolean {
    return this.set("bookmark", location);
  },

  /**
   * Establece los datos de suspensión
   */
  setSuspendData(jsonOrString: string): boolean {
    if (!is2004(scormVersion) && jsonOrString.length > 4000) {
      console.warn(`[SCORM] suspend_data excede límite (${jsonOrString.length}/4096)`);
    }
    return this.set("suspendData", jsonOrString);
  },

  /**
   * Establece el estado de completitud
   * SCORM 1.2: passed, completed, failed, incomplete, browsed, not attempted
   */
  setCompletion(state: "completed" | "incomplete" | "not attempted" | "browsed" | "passed" | "failed"): boolean {
    return this.set("completion", state);
  },

  /**
   * Establece el estado de éxito
   */
  setSuccess(state: "passed" | "failed" | "unknown"): boolean {
    if (!is2004(scormVersion) && state === "unknown") {
      return true; // No válido en 1.2
    }
    return this.set("success", state);
  },

  /**
   * Establece la puntuación
   */
  setScore(opts: { raw?: number; min?: number; max?: number; scaled?: number }): boolean {
    let ok = true;
    if (typeof opts.raw === "number") ok &&= this.set("scoreRaw", String(opts.raw));
    if (typeof opts.min === "number") ok &&= this.set("scoreMin", String(opts.min));
    if (typeof opts.max === "number") ok &&= this.set("scoreMax", String(opts.max));
    if (typeof opts.scaled === "number" && is2004(scormVersion)) {
      ok &&= this.set("scoreScaled", String(opts.scaled));
    }
    return ok;
  },

  /**
   * Establece el tiempo de sesión
   */
  setSessionTime(seconds: number): boolean {
    const formatted = is2004(scormVersion) ? secondsToISODur(seconds) : secondsToHMS(seconds);
    return this.set("sessionTime", formatted);
  },

  /**
   * Obtiene el tiempo total en segundos
   */
  getTotalTimeSeconds(): number {
    const tt = this.get("totalTime");
    return is2004(scormVersion) ? isoDurToSeconds(tt) : hmsToSeconds(tt);
  },

  /**
   * Obtiene el estado actual del SCORM
   */
  status() {
    return {
      version: scormVersion,
      completion: this.get("completion"),
      success: this.get("success"),
      scoreRaw: this.get("scoreRaw"),
      bookmark: this.get("bookmark"),
      totalTime: this.get("totalTime"),
    };
  },

  /**
   * Obtiene el último código de error
   */
  getLastError(): string {
    return apiGetLastError(API, scormVersion!);
  },

  /**
   * Obtiene la referencia directa a la API SCORM
   */
  getAPI(): ScormAPI {
    return API;
  },

  /**
   * Obtiene la versión de SCORM detectada
   */
  getVersion(): Version | null {
    return scormVersion;
  },

  /**
   * Muestra información de diagnóstico en la consola
   */
  debug() {
    console.group("[SCORM] Diagnóstico");
    console.log("Versión:", scormVersion);
    console.log("Conectado:", this.isConnected());
    console.log("API:", API);
    console.log("Último error:", apiGetLastError(API, scormVersion!));
    console.log("bookmark:", this.get("bookmark"));
    console.log("suspend_data length:", this.get("suspendData")?.length || 0);
    console.log("completion:", this.get("completion"));
    console.groupEnd();

    return {
      version: scormVersion,
      isConnected: this.isConnected(),
      lastError: apiGetLastError(API, scormVersion!)
    };
  },
};

// Exponer en window para debugging
(window as any).scormU = scormU;